import React, { useEffect, useState } from 'react';
import styles from './PredefinedPhrasesList.module.scss';
import AddButton from '../../../../Components/Button/AddButton';
import PredefinedPhrasesFormModal from './PredefinedPhrasesFormModal';
import PredefinedPhrasesListItem from './PredefinedPhrasesListItem/PredefinedPhrasesListItem';
import FlatList from '../../../Base/Components/FlatList';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import {
	Phrase,
	PhraseType,
	useMyPhrasesQuery,
} from '../../../../graphql/graphql';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';

interface Props {
	type: PhraseType;
	onEdit?: () => void;
}

export interface PredefinedPhrase {
	id: string;
	rank: number;
	value: string;
	type: PhraseType;
	name: string;
	meta: {
		id: string;
		key: string;
		value: string;
		formattedValue: string;
	};
}

const WRAPPER_ID = 'predefinedPhrasesListWrapper';

const PredefinedPhrasesList: React.FC<Props> = ({ type, onEdit }) => {
	const [isNewOpen, setNewOpen] = useState(false);

	const { flatData, pagination, getNextPage, appendData, updateItem, reset } =
		useFlatDataList<Phrase>();

	const { data, isLoading, refetch } = useMyPhrasesQuery({
		where: { type },
		pagination,
	});

	useEffect(() => {
		appendData((data?.myPhrases?.data as Phrase[]) ?? []);
	}, [data]);

	const handleOnEdit = (data: Phrase) => {
		updateItem(data);
		onEdit?.();
	};

	const handleOnCreateNewPhrase = (data: Phrase) => {
		setNewOpen(false);
		refresh();
	};

	const handleOnDelete = () => {
		refresh();
		onEdit?.();
	};

	const refresh = () => {
		reset();
		refetch();
	};

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.controls}>
					<AddButton
						onClick={() => setNewOpen(true)}
						title='component.predefinedPhrasesList.addButton'
					/>
				</div>
				<ScrollContainer wrapperId={WRAPPER_ID}>
					<FlatList
						onRefresh={refetch}
						onLoadMore={getNextPage}
						data={flatData}
						isLoading={isLoading}
						hasMoreData={data?.myPhrases?.meta?.hasNextPage || false}
						scrollableTargetId={WRAPPER_ID}
						itemKey={'id'}
						className={styles.list}
						renderItem={item => (
							<PredefinedPhrasesListItem
								type={type}
								item={item}
								key={item.id}
								onEdit={handleOnEdit}
								onDelete={handleOnDelete}
							/>
						)}
					/>
				</ScrollContainer>
			</div>
			{isNewOpen && (
				<PredefinedPhrasesFormModal
					isOpen
					onClose={() => setNewOpen(false)}
					type={type}
					onSuccess={handleOnCreateNewPhrase}
				/>
			)}
		</>
	);
};

export default PredefinedPhrasesList;
